import parse, {
  domToReact,
  Element,
  type DOMNode,
  type HTMLReactParserOptions,
} from 'html-react-parser';
import { useState } from 'react';

import { DeepResearchCard, DeepResearchCompContent, DeepResearchCompTitle } from '../deep-research-shared';

import type { AnswerClaim, TopicAnalysisAnswer } from '@/entities/ask-syncly/ask-syncly-types';
import { ReferencedPostsModal } from '@/features/(insight)/referenced-posts/ui';
import { CLAIM_MAX_POST_COUNT } from '@/features/(insight)/referenced-posts/ui';
import { cn } from '@/shared/utils/cn';
import { mdConverter } from '@/shared/utils/md-converter';
import { captureException } from '@sentry/react';

export function Topic({
  session_id,
  answer,
  block_id,
}: {
  session_id: string;
  answer: TopicAnalysisAnswer;
  block_id: string;
}) {
  const { title, content, overview } = answer;
  const [refModalOpen, setRefModalOpen] = useState<null | string>(null);
  const onClaimClick = (id: string) => {
    setRefModalOpen(id);
  };
  return (
    <DeepResearchCard>
      <DeepResearchCompTitle>{title}</DeepResearchCompTitle>
      <DeepResearchCompContent className="mt-5">{content}</DeepResearchCompContent>
      <h3 className="mt-16 text-xl font-bold">Overview</h3>
      <div className="mt-5 text-base text-gray-700">
        <ClaimContent
          content={overview.content}
          claims={overview.claims}
          onClaimClick={onClaimClick}
        />
      </div>
      {block_id && refModalOpen && (
        <ReferencedPostsModal
          isOpen={refModalOpen !== null}
          onClose={() => setRefModalOpen(null)}
          session_id={session_id}
          block_id={block_id}
          answer_id={answer.id}
          parts="overview"
          part_id={refModalOpen}
        />
      )}
    </DeepResearchCard>
  );
}

const ClaimContent = (props: {
  content: string;
  claims: AnswerClaim[];
  onClaimClick: (id: string) => void;
}) => {
  const { content, onClaimClick } = props;

  const htmlString = mdConverter.makeHtml(content);

  const options: HTMLReactParserOptions = {
    replace: (domNode) => {
      if (domNode instanceof Element && 'attribs' in domNode && domNode.name === 'claim') {
        const id = domNode?.attribs?.['data-claim-id'];
        const count = Number(domNode?.attribs?.['data-num-grounding-posts']);

        if (!id || typeof count !== 'number') {
          captureException(new Error('Invalid claim'));
          return null;
        }

        return (
          <ClaimItem
            id={id}
            selected={false}
            count={count}
            onClick={(e) => {
              e.preventDefault();
              onClaimClick(id);
            }}
          />
        );
      }
      if (domNode instanceof Element && 'attribs' in domNode && domNode.name === 'p') {
        return <div>{domToReact(domNode.children as DOMNode[], options)}</div>;
      }
    },
  };

  const parsed = parse(htmlString, options);

  return parsed;
};

type ClaimItemProps = {
  id: string;
  selected: boolean;
  onClick: (e: React.MouseEvent<HTMLAnchorElement>) => void;
  count: number;
};

export const ClaimItem = (props: ClaimItemProps) => {
  const { onClick, count, selected } = props;
  return (
    <span
      className={cn(
        'mx-1 inline-block -translate-y-0.5 cursor-pointer rounded-full border bg-gray-200 px-1.5 text-xs font-semibold transition-colors duration-200 hover:bg-gray-300',
        selected && 'bg-gray-800 text-white hover:bg-gray-800',
      )}
      onClick={onClick}
    >
      {count >= CLAIM_MAX_POST_COUNT ? `${CLAIM_MAX_POST_COUNT}+` : count}
    </span>
  );
};
